<script setup lang="ts">
const formData = reactive({
  firstName: "",
  lastName: "",
  email: "",
  company: "",
  message: "",
  optIn: false,
});

interface FormErrors {
  firstName: boolean;
  lastName: boolean;
  email: boolean;
  message: boolean;
}

const formErrors = ref<FormErrors>({
  firstName: false,
  lastName: false,
  email: false,
  message: false,
});

const hasSubmitted = ref(false);
const isSubmitting = ref(false);
const submissionError = ref(false);

function onCheckForm(): void {
  let hasErrors = false;

  if (formData.firstName == "") {
    formErrors.value.firstName = true;
  } else {
    formErrors.value.firstName = false;
  }

  if (formData.lastName == "") {
    formErrors.value.lastName = true;
  } else {
    formErrors.value.lastName = false;
  }

  if (!validEmail(formData.email)) {
    formErrors.value.email = true;
  } else {
    formErrors.value.email = false;
  }

  if (formData.message == "") {
    formErrors.value.message = true;
  } else {
    formErrors.value.message = false;
  }

  for (const [key, value] of Object.entries(formErrors.value)) {
    if (value) {
      hasErrors = true;
    }
  }

  if (!hasErrors) {
    submitForm();
  }
}

async function submitForm() {
  isSubmitting.value = true;
  submissionError.value = false;

  try {
    await $fetch('/api/contact', {
      method: 'POST',
      body: formData,
    });

    hasSubmitted.value = true;
  } catch (error) {
    submissionError.value = true;
    console.error('Contact form error', error);
  } finally {
    isSubmitting.value = false;
  }
}

function validEmail(email: string): boolean {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(email);
}
</script>

<template>
  <div
    class="home-section contact-block"
    :style="{ backgroundImage: `url(${useCdnUrl()}/assets/backgrounds/contact-background.webp)` }"
  >
    <div class="container">
      <h2>Contact <b>Us</b></h2>

      <ClientOnly>
        <div v-if="!hasSubmitted" class="contact-form">
          <div v-if="isSubmitting" class="submission-overlay" role="status" aria-live="polite">
            <Spinner aria-hidden="true" />
            <span>Submitting...</span>
          </div>
          <form>
          <div class="form-field">
            <label for="firstName" class="sr-only">First name</label>
            <input
              id="firstName"
              v-model="formData.firstName"
              type="text"
              name="firstName"
              placeholder="First name"
              required
            />
            <p v-if="formErrors.firstName" class="invalid">* Please enter your first name</p>
          </div>
          <div class="form-field">
            <label for="lastName" class="sr-only">Last name</label>
            <input
              id="lastName"
              v-model="formData.lastName"
              type="text"
              name="lastName"
              placeholder="Last name"
              required
            />
            <p v-if="formErrors.lastName" class="invalid">* Please enter your last name</p>
          </div>
          <div class="form-field">
            <label for="email" class="sr-only">Work email</label>
            <input
              id="email"
              v-model="formData.email"
              type="email"
              name="email"
              placeholder="Work email"
              required
            />
            <p v-if="formErrors.email" class="invalid">* Please enter a valid email</p>
          </div>
          <div class="form-field">
            <label for="company" class="sr-only">Company</label>
            <input
              id="company"
              v-model="formData.company"
              type="text"
              name="company"
              placeholder="Company"
            />
          </div>
          <div class="form-field span-2">
            <label for="message" class="sr-only">Tell us about your project</label>
            <textarea
              id="message"
              v-model="formData.message"
              name="message"
              placeholder="Tell us about your project"
              required
            ></textarea>
            <p v-if="formErrors.message" class="invalid">
              * Please provide us details of how we can help you
            </p>
          </div>

          <div class="disclaimers span-2">
            <p>
              Zenapptic will only use your personal information to provide the services you
              requested from us. We would like to contact you about our services, as well as other
              content that may be of interest to you. You may unsubscribe from this communication at
              any time.
            </p>

            <div class="option">
              <input id="optIn" v-model="formData.optIn" type="checkbox" name="optIn" />
              <label for="optIn"
                >I agree to receive informational and marketing communications from
                Zenapptic.</label
              >
            </div>
          </div>
          </form>

        <div class="span-2 submit-block">
          <button
            class="the-cta-button"
            type="button"
            aria-label="submit contact form"
            :disabled="isSubmitting"
            @click="onCheckForm"
          >
            <div>SEND</div>
          </button>
          <p v-if="submissionError" class="submit-error">Unable to submit your request. Please try again.</p>
        </div>
        </div>

        <div v-if="hasSubmitted" class="submit-response">
          <h3>Thanks for getting in touch!</h3>
          <p>We will respond to your request as soon as we can!</p>
        </div>
      </ClientOnly>
    </div>
  </div>
</template>

<style lang="scss">
@use "../assets/scss/vars";
.contact-block {
  background-color: #1a1719;
  color: #fff;

  .container {
    h2 {
      font-weight: 200;

      @media only screen and (max-width: 768px) {
        font-size: min(7vw, 4rem);
        margin: 0;
      }
    }

    .contact-form {
      font-size: min(4vw, 2.6rem);
      margin: auto;
      max-width: 700px;
      position: relative;
      width: 55%;

      .submission-overlay {
        align-items: center;
        background: rgb(26 23 25 / 88%);
        display: flex;
        flex-direction: column;
        gap: 1rem;
        inset: 0;
        justify-content: center;
        position: absolute;
        z-index: 1;

        span {
          font-size: 1.8rem;
        }
      }

      @media only screen and (max-width: 768px) {
        width: 100%;
      }

      form {
        display: grid;
        grid-row-gap: 3rem;

        @media only screen and (min-width: 769px) {
          grid-column-gap: 4rem;
          grid-template-columns: 1fr 1fr;
        }

        @media only screen and (max-width: 768px) {
          display: block;
        }

        .form-field {
          @media only screen and (max-width: 768px) {
            margin: 2rem 1rem;
          }

          input,
          textarea {
            color: #666;
            font-family: "pragmatica", sans-serif;
            font-size: 2rem;
            padding: 0.5rem 1rem;
            width: 100%;
          }

          textarea {
            min-height: 12rem;
          }

          .invalid {
            font-size: 1.5rem;
            margin: 0.3rem 0;
          }
        }

        .disclaimers {
          display: flex;
          flex-direction: column;
          gap: 3rem;

          @media only screen and (max-width: 768px) {
            gap: 1rem;
          }

          p,
          label {
            color: #fff;
            font-size: min(4vw, 1.8rem);
            margin: 0;
          }

          p {
            @media only screen and (max-width: 768px) {
              margin: 2rem 1rem;
            }
          }

          .option {
            align-items: start;
            display: flex;
            gap: 1rem;

            input {
              margin-top: 5px;
            }
          }
        }
      }

      .submit-block {
        padding-top: 3rem;
        text-align: center;
      }
    }

    .submit-response {
      padding: 7rem;
      text-align: center;
      h3 {
        font-size: 2.4rem;
      }
      p {
        font-size: 2rem;
      }
    }

    .span-2 {
      grid-column: 1 / span 2;
    }
  }

  button.the-cta-button {
    background-color: transparent;
    border: 2px solid white;
    color: white;
    cursor: pointer;
    display: inline-block;
    margin: auto;
    padding: 1px;
    text-decoration: none;
    div {
      border: 1px solid white;
      font-size: 2rem;
      font-weight: 700;
      padding: 0.75rem 6rem;
      text-align: center;
    }

    &:disabled {
      cursor: wait;
      opacity: 0.65;
    }

    &:hover {
      border-color: rgb(150, 150, 150);

      div {
        border-color: rgb(150, 150, 150);
      }
    }
  }

  .submit-error {
    color: #ffb3b3;
    font-size: 1.6rem;
    margin: 1rem 0 0;
    text-align: center;
  }
}
</style>
